import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { Activity } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import localSettings from "@app/config/LocalSettings";
import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import {
  getCheckInsDrillDownTotalFilteredRecords,
  getCheckInsDrillDownViewState,
} from "@app/scene/dashboard/store/cards/checkIns/selectors";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import expandColumns from "@app/scene/dashboard/utils/expandColumns";
import { callApi } from "@app/store/api/callApi";
import { fetchCheckInsCardDataHelper } from "@app/store/dashboard/cardDataFetchHelpers";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import getDefaultDrillDownColumns from "../../utils/getDefaultDrillDownColumns";

import {
  applyCheckInsDrillDownListViewSettings,
  exportCheckInsCardDrillDownData,
  fetchCheckInsCardData,
  fetchCheckInsCardDrillDownData,
  showCheckInsDrillDown,
} from "./actions";

const defaultColumns: string[] = [
  ActivityFieldName.PRIMARY_ASSOCIATION,
  ActivityFieldName.ACTIVITY_TYPE,
  ActivityFieldName.ASSIGNEE,
  ActivityFieldName.COMPLETED_AT,
  ActivityFieldName.CHECK_IN_DISTANCE,
  ActivityFieldName.RELIABILITY,
  ActivityFieldName.NOTE,
];

const defaultSortColumn = ActivityFieldName.COMPLETED_AT;

function* onFetchCheckInsCardData({ payload }: ReturnType<typeof fetchCheckInsCardData>) {
  const { callback, configuration, failureCallback, scope } = payload;

  try {
    yield call(fetchCheckInsCardDataHelper, { callback, configuration, scope });
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onOpenDrillDown({ payload }: ReturnType<typeof showCheckInsDrillDown.request>) {
  const { configuration, customDateRange, customReliability, scope } = payload;
  const { criteria, dateRange } = configuration;

  const drillDownViewState: DrillDownViewState = yield select(getCheckInsDrillDownViewState);

  const viewState = localSettings.getViewSettings(`dashboard/checkIns`, activityFieldModel, {
    ...drillDownViewState,
    columns: getDefaultDrillDownColumns(defaultColumns, activityFieldModel),
    sort: [
      {
        field: activityFieldModel.getByName(defaultSortColumn)!,
        order: SortOrder.DESC,
      },
    ],
  });

  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const range = customDateRange ?? getDatesRange(dateRange.range, dateRange.subRange);

  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope.teamId) : undefined;
  const assigneeIds = scope?.userId ? [scope.userId] : scopeTeam?.users.map(({ id }) => id);

  const filter: FilterModel = createAndCondition([
    createSimpleCondition(ActivityFieldName.COMPLETED_AT, FilterOperator.IN_RANGE, [
      range.startDate,
      range.endDate,
    ]),
    customReliability !== undefined
      ? createSimpleCondition(
          ActivityFieldName.RELIABILITY,
          FilterOperator.EQUALS,
          customReliability
        )
      : createSimpleCondition(ActivityFieldName.RELIABILITY, FilterOperator.NOT_EMPTY),
  ]);
  if (criteria.activityTypeIds.length) {
    filter.conditions.push(
      createSimpleCondition(
        ActivityFieldName.ACTIVITY_TYPE,
        FilterOperator.IN_ANY,
        criteria.activityTypeIds
      )
    );
  }
  if (assigneeIds) {
    filter.conditions.push(
      createSimpleCondition(ActivityFieldName.ASSIGNEE, FilterOperator.IN_ANY, assigneeIds)
    );
  }
  if (criteria.sources.length) {
    filter.conditions.push(
      createSimpleCondition(ActivityFieldName.SOURCE, FilterOperator.IN_ANY, criteria.sources)
    );
  }
  viewState.filter = filter;

  yield put(showCheckInsDrillDown.success({ viewState }));
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchCheckInsCardDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: activities logged card, onFetchDrillDownData", payload);
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete payload.request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyCheckInsDrillDownListViewSettings(payload.request));
    }

    const drillDownViewState: DrillDownViewState = yield select(getCheckInsDrillDownViewState);

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(drillDownViewState, `dashboard/checkIns`);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          activityFieldModel
        ),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const response: ListResponse<Activity> = yield callApi(
      "fetchActivities",
      orgId,
      requestPayload
    );
    payload.dataCallback?.(response);
    yield put(
      fetchCheckInsCardDrillDownData.success({
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    payload.failCallback?.();
    yield put(fetchCheckInsCardDrillDownData.failure(error));
    yield put(handleError({ error }));
  }
}

function* onExport() {
  const drillDownViewState: DrillDownViewState = yield select(getCheckInsDrillDownViewState);
  const total: number = yield select(getCheckInsDrillDownTotalFilteredRecords);

  yield put(
    exportEntities.request({
      entityType: EntityType.ACTIVITY,
      total,
      viewState: expandColumns(drillDownViewState),
    })
  );
}

export function* checkInsSagas() {
  // we use takeEvery because there might be several cards of such type on board
  // however, it would be more optimal to use takeLatest, but also filter by card ids
  yield takeEvery(fetchCheckInsCardData, onFetchCheckInsCardData);
  yield takeLatest(showCheckInsDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchCheckInsCardDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportCheckInsCardDrillDownData, onExport);
}
